import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  LogLevel,
  BrowserCacheLocation,
} from '@azure/msal-browser';
import type { Configuration } from '@azure/msal-browser';
import { environment } from '../environments/environment';

const msalConfig: Configuration = {
  auth: {
    clientId: environment.clientID,
    authority: environment.authority,
    redirectUri: environment.redirectUri,
    // Disable MSAL's built-in navigation so React Router handles it.
    // AuthGuard saves the intended path to sessionStorage before loginRedirect,
    // then navigates back via useNavigate once loggedIn becomes true.
    navigateToLoginRequestUrl: false,
  },
  cache: {
    // Use localStorage so Angular remote can access the same cached tokens
    // Both apps share the same Azure AD client ID, so they can reuse each other's tokens
    cacheLocation: BrowserCacheLocation.LocalStorage,
    storeAuthStateInCookie: false, // Cookies are blocked in private browsing
  },  
  system: {
    allowRedirectInIframe: true, // Allow MSAL to work in iframes (e.g. embedded PowerBI)
    loggerOptions: {
      loggerCallback: (_level: LogLevel, message: string) => {
        console.log('[MSAL]', message);
      },
      logLevel: LogLevel.Verbose,
      piiLoggingEnabled: false,
    },
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL once at module level
export const msalInitPromise = msalInstance.initialize();

// *** Pre-call handleRedirectPromise immediately after initialize ***
// MSAL consumes the auth code from the URL exactly once. If we call it
// inside a React hook, StrictMode's double-mount means the second call
// returns null (code already consumed). Calling it here at module level
// guarantees the auth code is captured before any React rendering.
export const redirectResultPromise = msalInitPromise.then(() =>
  msalInstance.handleRedirectPromise()
);

// Track whether initialization has completed synchronously
let msalInitialized = false;
msalInitPromise.then(() => { msalInitialized = true; });

/**
 * Safe wrapper: returns the active account only if MSAL has been initialized.
 * Use this in components that render before init completes.
 */
export function getActiveAccountSafe() {
  if (!msalInitialized) return null;
  return msalInstance.getActiveAccount();
}

/**
 * Safe wrapper: returns all accounts only if MSAL has been initialized.
 */
export function getAllAccountsSafe() {
  if (!msalInitialized) return [];
  return msalInstance.getAllAccounts();
}

export const loginRequest = {
  scopes: ['user.read'],
};

export const apiTokenRequest = {
  scopes: [environment.apiScope, 'openid', 'profile'],
};

/** sessionStorage key AuthGuard/redirect helpers use to restore the pre-login path. */
export const RETURN_PATH_KEY = 'auth_return_path';

/**
 * True when a failed silent token acquisition means the refresh token is
 * dead and only an interactive login can recover (e.g. AADSTS70043 — refresh
 * token expired/revoked due to conditional-access sign-in frequency).
 *
 * MSAL normally throws InteractionRequiredAuthError for this, but AAD does not
 * always tag the response with an `interaction_required` suberror — sign-in
 * frequency revocations can come back as a plain invalid_grant ServerError
 * instead, which MSAL does *not* upgrade to InteractionRequiredAuthError. So
 * we also inspect the raw error content as a fallback.
 */
export function isInteractionRequiredError(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) return true;
  const err = error as
    | { errorCode?: string; subError?: string; errorMessage?: string; message?: string }
    | null
    | undefined;
  const haystack = [err?.errorCode, err?.subError, err?.errorMessage, err?.message]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return (
    haystack.includes('interaction_required') ||
    haystack.includes('invalid_grant') ||
    haystack.includes('token_expired') ||
    haystack.includes('70043')
  );
}

let reauthRedirectInFlight = false;

/**
 * Send the user through an interactive login when silent refresh can no
 * longer produce a token. Guarded against concurrent callers (several
 * in-flight API calls can all hit the same expired refresh token at once)
 * so only one redirect is ever kicked off.
 */
export function redirectToReAuthenticate(): Promise<void> {
  if (reauthRedirectInFlight) return Promise.resolve();
  reauthRedirectInFlight = true;

  const intendedPath = window.location.pathname + window.location.search;
  if (intendedPath && intendedPath !== '/') {
    sessionStorage.setItem(RETURN_PATH_KEY, intendedPath);
  }

  return msalInstance.acquireTokenRedirect(apiTokenRequest).catch((err) => {
    console.error('[msalConfig] Failed to redirect for re-authentication', err);
    reauthRedirectInFlight = false;
  });
}

/**
 * Acquire a bearer access token for calling protected KCi APIs.
 * Uses silent token acquisition; returns null if no active account or if the
 * refresh token is dead (in which case it also kicks off an interactive
 * re-login redirect — caller should treat null as "request cannot proceed").
 */
export async function getAccessToken(): Promise<string | null> {
  await msalInitPromise;
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return null;
  try {
    const response = await msalInstance.acquireTokenSilent({
      ...apiTokenRequest,
      account,
    });
    return response.accessToken ?? null;
  } catch (error) {
    console.warn('[msalConfig] acquireTokenSilent failed', error);
    if (isInteractionRequiredError(error)) {
      await redirectToReAuthenticate();
    }
    return null;
  }
}
